import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Globe, Droplet, Mail, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { TEMPLATES, Watermark } from "@/pages/InvoiceTemplates";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "IDR", label: "IDR (Rupiah)" }, { code: "USD", label: "USD (Dollar)" },
  { code: "SGD", label: "SGD (Singapore)" }, { code: "EUR", label: "EUR (Euro)" },
  { code: "GBP", label: "GBP (Pound)" }, { code: "AUD", label: "AUD (Australia)" },
  { code: "MYR", label: "MYR (Ringgit)" }, { code: "JPY", label: "JPY (Yen)" },
];

const ITEMS_PER_PAGE = 15;

function chunk(arr, n) {
  const out = []; for (let i=0; i<arr.length; i+=n) out.push(arr.slice(i, i+n));
  return out.length ? out : [[]];
}

export default function PrintInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [party, setParty] = useState(null);
  const [owner, setOwner] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lang, setLang] = useState("id");
  const [currency, setCurrency] = useState("IDR");
  const [rate, setRate] = useState(1);
  const [rateInfo, setRateInfo] = useState("");
  const [rateBusy, setRateBusy] = useState(false);
  const [watermark, setWatermark] = useState("auto");
  const [customWm, setCustomWm] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pagesRef = useRef([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      const [me, s, p] = await Promise.all([
        api.get("/auth/me"), api.get("/settings"),
        data.party_id ? api.get("/parties").then(r => r.data.find(x => x.id === data.party_id)) : Promise.resolve(null),
      ]);
      setOwner(me.data); setSettings(s.data);
      if (s.data.logo_file_id) {
        const t = localStorage.getItem("token");
        setLogoUrl(`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.data.logo_file_id}?auth=${t}`);
      }
      setParty(p);
      setEmailTo(p?.email || "");
      const font = s.data.invoice_font || "Outfit";
      if (font !== "Outfit" && !document.getElementById("print-font-" + font.replace(/\s+/g,"-"))) {
        const l = document.createElement("link"); l.id = "print-font-" + font.replace(/\s+/g,"-"); l.rel = "stylesheet";
        l.href = `https://fonts.googleapis.com/css2?family=${font.replace(/\s+/g,"+")}:wght@400;500;700&display=swap`;
        document.head.appendChild(l);
      }
    })();
  }, [id]);

  const fetchRate = async (code) => {
    if (code === "IDR") { setRate(1); setRateInfo(""); return; }
    setRateBusy(true);
    try {
      const { data } = await api.get(`/exchange-rate?currency=${code}`);
      setRate(data.idr_per_unit);
      setRateInfo(`Kurs ${new Date(data.fetched_at).toLocaleString("id-ID", { hour:"2-digit", minute:"2-digit", day:"2-digit", month:"short" })} · ${data.source}`);
    } catch { toast.error("Gagal ambil kurs otomatis"); } finally { setRateBusy(false); }
  };

  const onCurrencyChange = (code) => { setCurrency(code); setRate(1); setRateInfo(""); if (code !== "IDR") fetchRate(code); };

  const sendEmail = async () => {
    if (!emailTo) return toast.error("Isi email penerima");
    setEmailBusy(true);
    try {
      await api.post(`/invoices/${id}/send-email`, { to: emailTo, message: emailMsg || null });
      toast.success(`Invoice terkirim ke ${emailTo}`);
      setEmailOpen(false); setEmailMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal kirim email"); } finally { setEmailBusy(false); }
  };

  const scrollToPage = (idx) => {
    setCurrentPage(idx);
    pagesRef.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!inv) return <div className="p-12 text-forest-400">Memuat…</div>;

  const isSales = inv.kind === "sales";
  const brandName = settings?.brand_name || owner?.name || "Perusahaan";
  const font = settings?.invoice_font || "Outfit";
  const primary = settings?.primary_color || "#151A18";
  const accent = settings?.accent_color || "#D99C3B";
  const Template = TEMPLATES[settings?.invoice_template || "classic"] || TEMPLATES.classic;

  let wmText = "";
  if (watermark === "auto") {
    if (inv.status === "paid") wmText = lang === "en" ? "PAID" : "LUNAS";
    else if (inv.status === "cancelled") wmText = lang === "en" ? "VOID" : "BATAL";
  } else if (watermark === "custom") wmText = customWm;
  else if (watermark !== "none") wmText = watermark;

  const pages = chunk(inv.items || [], ITEMS_PER_PAGE);
  const totalPages = pages.length;

  return (
    <div className="min-h-screen bg-[#F5F6F4] text-[#151A18]">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-page { background: white !important; box-shadow: none !important; margin: 0 !important; page-break-after: always; }
          .print-page:last-of-type { page-break-after: auto; }
          @page { size: A4; margin: 14mm }
        }
      `}</style>

      <div className="no-print sticky top-0 z-30 bg-white border-b border-forest-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Globe size={14} className="text-forest-400"/>
            <div className="flex rounded-sm overflow-hidden border border-forest-100">
              <button data-testid="btn-lang-id" onClick={()=>setLang("id")} className={`px-3 py-1 text-xs ${lang==="id"?"bg-[#2D4739] text-white":"bg-white text-forest-400"}`}>ID</button>
              <button data-testid="btn-lang-en" onClick={()=>setLang("en")} className={`px-3 py-1 text-xs ${lang==="en"?"bg-[#2D4739] text-white":"bg-white text-forest-400"}`}>EN</button>
            </div>
          </div>

          <div className="flex items-center gap-1 text-sm">
            <Select value={currency} onValueChange={onCurrencyChange}>
              <SelectTrigger data-testid="sel-currency" className="h-8 w-36 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code} className="text-xs">{c.label}</SelectItem>)}</SelectContent>
            </Select>
            {currency !== "IDR" && (
              <>
                <Input data-testid="inp-rate" type="number" value={rate} onChange={e=>setRate(+e.target.value)} className="h-8 w-24 text-xs"/>
                <button onClick={()=>fetchRate(currency)} disabled={rateBusy} data-testid="btn-refresh-rate" className="p-1.5 hover:bg-forest-50 rounded-sm" title="Ambil kurs realtime">
                  <RefreshCw size={12} className={rateBusy?"animate-spin":""}/>
                </button>
                {rateInfo && <span className="text-[10px] text-forest-400">{rateInfo}</span>}
              </>
            )}
          </div>

          <div className="flex items-center gap-1 text-sm">
            <Droplet size={14} className="text-forest-400"/>
            <Select value={watermark} onValueChange={setWatermark}>
              <SelectTrigger data-testid="sel-watermark" className="h-8 w-32 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Otomatis</SelectItem>
                <SelectItem value="none">Tanpa</SelectItem>
                <SelectItem value="LUNAS">LUNAS</SelectItem>
                <SelectItem value="BELUM LUNAS">BELUM LUNAS</SelectItem>
                <SelectItem value="DUPLIKAT">DUPLIKAT</SelectItem>
                <SelectItem value="DRAFT">DRAFT</SelectItem>
                <SelectItem value="PAID">PAID</SelectItem>
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {watermark === "custom" && <Input placeholder="Teks…" value={customWm} onChange={e=>setCustomWm(e.target.value)} className="h-8 w-24 text-xs"/>}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1 text-sm border border-forest-100 rounded-sm px-2 py-1">
              <button onClick={()=>scrollToPage(Math.max(0, currentPage-1))} disabled={currentPage===0} className="p-1 disabled:opacity-30"><ChevronLeft size={14}/></button>
              <span data-testid="page-indicator" className="text-xs text-forest-400 min-w-[3rem] text-center">{currentPage+1} / {totalPages}</span>
              <button onClick={()=>scrollToPage(Math.min(totalPages-1, currentPage+1))} disabled={currentPage===totalPages-1} className="p-1 disabled:opacity-30"><ChevronRight size={14}/></button>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="btn-send-email" variant="outline" onClick={()=>setEmailOpen(true)} className="rounded-sm gap-2">
              <Mail size={14}/> Kirim Email
            </Button>
            <Button data-testid="btn-print" onClick={()=>window.print()} style={{ backgroundColor: primary }} className="rounded-sm text-white gap-2">
              <Printer size={14}/> Cetak / PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {pages.map((pageItems, idx) => {
          const isFirst = idx === 0, isLast = idx === totalPages - 1;
          const pageInv = { ...inv, items: pageItems };
          return (
            <div key={idx} ref={el => pagesRef.current[idx] = el} className="print-page bg-white shadow-md relative overflow-hidden">
              <Watermark text={wmText}/>
              {totalPages > 1 && <div className="absolute top-4 right-4 z-20 text-[10px] uppercase tracking-widest text-forest-400 no-print">Hal {idx+1} / {totalPages}</div>}
              <div className="relative z-10">
                <Template inv={pageInv} party={party} owner={owner} settings={isLast ? settings : { ...settings, bank_accounts: [], invoice_footer: null }}
                          logoUrl={logoUrl} font={font} primary={primary} accent={accent}
                          isSales={isSales} brandName={brandName} lang={lang} currency={currency} rate={rate}/>
                {!isLast && <div className="text-center text-xs text-forest-400 pb-6 italic">— bersambung ke halaman {idx+2} —</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-[#FDFDFB] rounded-sm">
          <DialogHeader><DialogTitle className="font-display">Kirim Invoice via Email</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div><label className="text-xs text-forest-400">Email Penerima</label><Input data-testid="inp-email-to" type="email" value={emailTo} onChange={e=>setEmailTo(e.target.value)} placeholder="klien@example.com"/></div>
            <div><label className="text-xs text-forest-400">Pesan Tambahan (opsional)</label><Textarea data-testid="inp-email-msg" value={emailMsg} onChange={e=>setEmailMsg(e.target.value)} placeholder="Halo, mohon dibayar sebelum jatuh tempo. Terima kasih." rows={3}/></div>
            <div className="text-xs text-forest-400">Email akan berisi ringkasan invoice + tombol untuk melihat & bayar via link publik.</div>
          </div>
          <DialogFooter><Button data-testid="btn-send-email-confirm" onClick={sendEmail} disabled={emailBusy} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">{emailBusy?"Mengirim…":"Kirim"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
